import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { toast } from '@grandvisionhq/common';
import { Util } from '@grandvisionhq/forms';
import { Alert } from '@grandvisionhq/iris-ui';
import { useFeatureToggles, useIntl } from '@grandvisionhq/state';
import { useTrackPageView } from '@grandvisionhq/svc-tealium/events';
import { DateUtils } from '@grandvisionhq/utils';
import { useAppointmentForm } from '../../context/AppointmentFormContext/AppointmentFormContext.js';
import { timeSelectionErrorEvent, bookAppointmentWhoStepScreenEvent } from '../../data-layer/actions.js';
import '@grandvisionhq/graphql';
import '../../queries/book-appointment.js';
import '../../queries/get-calendar-availability.js';
import '../../queries/get-time-slots.js';
import '../../queries/get-store-offer-by-code.js';
import '../../queries/get-user.js';
import '../../queries/get-store-opening-times.js';
import '../../queries/get-availability-stores-nearby.js';
import 'date-fns';
import '@swc/helpers';
import '@grandvisionhq/stores-v2';
import useTimeSlots from '../../hooks/api/use-time-slots.js';
import { useBookAppointment } from '../../hooks/api/use-book-appointment.js';
import { useUser } from '../../hooks/api/use-user.js';
import { useAuthToken } from '../../hooks/use-auth-token.js';
import useDataLayerInput from '../../hooks/use-data-layer-input.js';
import { useAppointmentContext } from '../../pages/BookAppointment/context/AppointmentContext.js';
import { useRoutingContext } from '../../pages/BookAppointment/context/RoutingContext/context.js';
import { BookAppointmentFormLayout } from '../../types/Forms.js';
import '../../types/shared.js';
import { extractBookAppointmentError } from '../../utils/extract-book-appointment-error.js';
import { setStoredAppointment } from '../../utils/book-appointment-storage.js';
import AppointmentPickDifferentTimeSlotContainer from '../AppointmentPickDifferentTimeSlot/AppointmentPickDifferentTimeSlotContainer.js';
import { AppointmentCustomerForm } from './AppointmentCustomerForm.js';
import { useScrollToField } from './hooks/use-scroll-to-field.js';

var // new errors (failures) with new book appointment mutation
Errors;
(function(Errors) {
    Errors["EXTERNAL_SERVER_ERROR"] = 'ExternalServerErrorFailure';
    Errors["APPOINTMENT_CREATION_ERROR"] = 'AppointmentCreationFailure';
    Errors["APPOINTMENT_MAX_EXCEEDED"] = 'AppointmentMaxExceededFailure';
    Errors["APPOINTMENT_ALREADY_BOOKED_OR_NOT_AVAILABLE"] = 'AlreadyBookedOrNotAvailableFailure';
})(Errors || (Errors = {}));
var // error label keys for labels configured in bloomreach
ErrorLabelKeys;
(function(ErrorLabelKeys) {
    ErrorLabelKeys["INTERNAL_SERVER_ERROR"] = "INTERNAL_SERVER_ERROR";
    ErrorLabelKeys["EXTERNAL_SERVER_ERROR"] = "EXTERNAL_SERVER_ERROR";
    ErrorLabelKeys["APPOINTMENT_CREATION_ERROR"] = "APPOINTMENT_CREATION_ERROR";
    ErrorLabelKeys["APPOINTMENT_MAX_EXCEEDED"] = "APPOINTMENT_MAX_EXCEEDED";
    ErrorLabelKeys["APPOINTMENT_ALREADY_BOOKED_OR_NOT_AVAILABLE"] = "APPOINTMENT_ALREADY_BOOKED_OR_NOT_AVAILABLE";
})(ErrorLabelKeys || (ErrorLabelKeys = {}));
const AppointmentCustomerFormContainer = ()=>{
    const { Form  } = useAppointmentForm();
    const [userQuery, userQueryState] = useUser();
    const [, authTokenState] = useAuthToken();
    const { isMyAccountEnabled  } = useFeatureToggles();
    const intl = useIntl();
    const { getLabel  } = intl;
    const [{ goTo  }, { queryParams  }] = useRoutingContext();
    const [{ setAppointment  }, { type , store , timeSlot , tryInStoreProductSkus  }] = useAppointmentContext();
    const layout = (type === null || type === void 0 ? void 0 : type.ageGroup) === 'CHILD' ? BookAppointmentFormLayout.CHILD : BookAppointmentFormLayout.DEFAULT;
    const [bookAppointment, bookAppointmentState] = useBookAppointment();
    const [errorLabelKey, setErrorLabelKey] = React.useState();
    const [redirecting, setRedirecting] = React.useState(false);
    const [firstInvalidField, setFirstInvalidField] = React.useState('');
    const [initialFormState, setInitialState] = React.useState();
    const [whoStepTimeSlot, setWhoStepTimeSlot] = React.useState(null);
    const [getTimeSlots, timeSlots] = useTimeSlots();
    const bookAppointmentError = extractBookAppointmentError(bookAppointmentState.data.failures);
    const showPickDifferentTimeSlot = bookAppointmentError === Errors.APPOINTMENT_ALREADY_BOOKED_OR_NOT_AVAILABLE;
    const disableSubmitForTimeSlot = showPickDifferentTimeSlot ? whoStepTimeSlot ? false : true : false;
    const authToken = authTokenState.data;
    const disableSubmit = bookAppointmentState.loading || disableSubmitForTimeSlot;
    const loading = authTokenState.loading || userQueryState.loading;
    const loggedIn = isMyAccountEnabled && Boolean(authToken);
    const toCamelCase = (str)=>str.toLowerCase().replace(/[-_][a-z]/g, (group)=>group.slice(-1).toUpperCase()
        )
    ;
    const errorLabelSuffix = errorLabelKey ? toCamelCase(errorLabelKey) : '';
    const alertTitle = !showPickDifferentTimeSlot ? getLabel(`appointments.component.bookAppointmentForm.alert.title.${errorLabelSuffix}`) : undefined;
    const alertMessage = getLabel(`appointments.component.bookAppointmentForm.alert.message.${errorLabelSuffix}`);
    const dataLayerInput = useDataLayerInput();
    React.useEffect(()=>{
        if (showPickDifferentTimeSlot) {
            const dataLayerEvent = timeSelectionErrorEvent(dataLayerInput);
            DataLayer.pushEvent(dataLayerEvent);
        }
    }, [
        dataLayerInput,
        showPickDifferentTimeSlot
    ]);
    React.useEffect(()=>{
        if (!userQueryState.data) {
            return;
        }
        const options = {
            intl,
            layout
        };
        setInitialState(Form === null || Form === void 0 ? void 0 : Form.fromDataToState(userQueryState.data, options));
    }, [
        Form,
        intl,
        layout,
        userQueryState.data
    ]);
    const onSubmit = React.useCallback((state)=>{
        if (!state.formValid) {
            /** Touch all fields to show invalid styles if has invalid fields */ setInitialState(Util.touchComposedFormFields(state.formState));
            toast.failed('appointments.component.bookAppointmentForm.validationError');
            var ref;
            /** Get first invalid fields name to use it as a reference to scroll to this field*/ setFirstInvalidField(Util.getFirstInvalidField(state.formState, (ref = Form === null || Form === void 0 ? void 0 : Form.getFormGroups({
                layout
            })) !== null && ref !== void 0 ? ref : []));
            return;
        }
        if (Form) {
            const formData = Form.fromStateToData(state.formState, {
                intl,
                layout
            }, {
                loggedIn
            });
            bookAppointment(authToken, formData);
        }
    }, [
        Form,
        intl,
        layout,
        authToken,
        bookAppointment,
        loggedIn
    ]);
    useScrollToField(firstInvalidField, ()=>setFirstInvalidField('')
    );
    React.useEffect(()=>{
        if (authToken) {
            userQuery(authToken);
        }
    }, [
        authToken,
        userQuery
    ]);
    React.useEffect(()=>{
        const bookedAppointment = bookAppointmentState.data.appointment;
        if (!bookedAppointment || redirecting) {
            return;
        }
        setRedirecting(true);
        setAppointment(bookedAppointment);
        setStoredAppointment(bookedAppointment);
        const { typeId , storeId , start , end  } = queryParams;
        goTo('confirmation', {
            typeId,
            storeId,
            start,
            end,
            appointmentId: bookedAppointment.id,
            tryInStoreProductSkus
        });
    // Only run on createdAppointment updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        bookAppointmentState
    ]);
    React.useEffect(()=>{
        const { typeId , storeId , start , end  } = queryParams;
        /** Need to track when user goes back/forth with browser navigation.
     * In this situation update event only with data based on query parameters.
     */ const hasEqualTimeSlotFromUrl = timeSlot && start === `${timeSlot.from.getTime()}` && end === `${timeSlot.until.getTime()}`;
        const canPushEvent = store && type && hasEqualTimeSlotFromUrl && Number(storeId) === store.code && typeId === type.id;
        if (canPushEvent) {
            const dataLayerEvent = bookAppointmentWhoStepScreenEvent(dataLayerInput);
            DataLayer.pushEvent(dataLayerEvent);
        }
    }, [
        queryParams,
        store,
        timeSlot,
        type,
        dataLayerInput
    ]);
    React.useEffect(()=>{
        if (bookAppointmentState.error) {
            // the only apollo error that is possible is internalServerError
            setErrorLabelKey(ErrorLabelKeys.INTERNAL_SERVER_ERROR);
            return;
        }
        const bookAppointmentFailures = bookAppointmentState.data.failures;
        if (bookAppointmentFailures && bookAppointmentFailures.length > 0) {
            var ref;
            const failureType = (ref = bookAppointmentFailures[0]) === null || ref === void 0 ? void 0 : ref.__typename;
            switch(failureType){
                case Errors.APPOINTMENT_ALREADY_BOOKED_OR_NOT_AVAILABLE:
                    setErrorLabelKey(ErrorLabelKeys.APPOINTMENT_ALREADY_BOOKED_OR_NOT_AVAILABLE);
                    break;
                case Errors.APPOINTMENT_CREATION_ERROR:
                    setErrorLabelKey(ErrorLabelKeys.APPOINTMENT_CREATION_ERROR);
                    break;
                case Errors.APPOINTMENT_MAX_EXCEEDED:
                    setErrorLabelKey(ErrorLabelKeys.APPOINTMENT_MAX_EXCEEDED);
                    break;
                case Errors.EXTERNAL_SERVER_ERROR:
                    setErrorLabelKey(ErrorLabelKeys.EXTERNAL_SERVER_ERROR);
                    break;
                default:
                    setErrorLabelKey(ErrorLabelKeys.INTERNAL_SERVER_ERROR);
            }
            return;
        }
        setErrorLabelKey(undefined);
    }, [
        bookAppointmentError,
        bookAppointmentState.data.failures,
        bookAppointmentState.error
    ]);
    /**
   * Pick different timeslot actions
   */ React.useEffect(()=>{
        // Reset timeslot value when PickDifferentTimeSlot component should be unmounted
        if (!showPickDifferentTimeSlot && whoStepTimeSlot) {
            setWhoStepTimeSlot(null);
        }
        const storeCode = store === null || store === void 0 ? void 0 : store.code;
        const appointmentTypeId = type === null || type === void 0 ? void 0 : type.id;
        const date = timeSlot === null || timeSlot === void 0 ? void 0 : timeSlot.from;
        if (!date || storeCode === undefined || appointmentTypeId === undefined) {
            // This should never happen, but it's here to satisfy typescript
            return;
        }
        getTimeSlots({
            variables: {
                storeCode: `${storeCode}`,
                appointmentTypeId,
                date: DateUtils.toLocalDate(date)
            }
        });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [
        showPickDifferentTimeSlot
    ]);
    const aabWhoPageView = React.useCallback(()=>new Promise((resolve)=>{
            if (!store || !type) {
                return;
            }
            resolve({
                id: 'VirtualPage-View',
                Page_Type: 'Info',
                Page_Section1: 'Exam',
                Page_Section2: 'PersonalInfo',
                Exam_Fitting: type.name,
                Store_Search_StoreId: store.code.toString()
            });
        })
    , [
        store,
        type
    ]);
    useTrackPageView(aabWhoPageView);
    return /*#__PURE__*/ React.createElement(AppointmentCustomerForm, {
        loading: loading,
        loggedIn: loggedIn,
        Form: Form,
        layout: layout,
        initialFormState: initialFormState,
        onSubmit: onSubmit,
        submitting: bookAppointmentState.loading,
        disableSubmit: disableSubmit,
        title: getLabel('appointments.component.bookAppointmentForm.title'),
        beforeSubmitSlot: showPickDifferentTimeSlot ? /*#__PURE__*/ React.createElement(AppointmentPickDifferentTimeSlotContainer, {
            onSelectTimeSlot: setWhoStepTimeSlot,
            selectedTimeSlot: whoStepTimeSlot,
            timeSlots: timeSlots.filtered.included,
            loading: timeSlots.loading
        }) : undefined,
        formAlert: errorLabelKey && /*#__PURE__*/ React.createElement(Alert, {
            variant: "warning",
            title: alertTitle,
            className: "alert-m-default"
        }, alertMessage)
    });
};

export { AppointmentCustomerFormContainer, AppointmentCustomerFormContainer as default };
//# sourceMappingURL=AppointmentCustomerFormContainer.js.map
